import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ContentActions } from './content.actions';
import {
  catchError,
  EMPTY,
  forkJoin,
  from,
  map,
  of,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { GoalmateProgramRepositoryService } from '@goalmate/repository';
import { Store } from '@ngrx/store';
import { contentsFeature } from './content.reducer';
import { GoalMateProgramDay } from '@goalmate/typings';

@Injectable()
export class ContentEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private goalMateProgramDB = inject(GoalmateProgramRepositoryService);

  loadContent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ContentActions.loadContents),
      switchMap(({ programId }) => {
        return forkJoin([
          from(this.goalMateProgramDB.getAllGroups(programId)),
          from(this.goalMateProgramDB.getProgramById(programId)),
        ]).pipe(
          switchMap(([contents, program]) =>
            of(ContentActions.loadContentsSuccess({ contents, program })),
          ),
          catchError((error) => {
            console.error('Error', error);
            return of(
              ContentActions.loadContentsError({
                error: error.code || 'Server error',
              }),
            );
          }),
        );
      }),
    );
  });

  loadPrivateContent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ContentActions.selectContent),
      withLatestFrom(
        this.store.select(contentsFeature.selectSelectedContent),
        this.store.select(contentsFeature.selectSelectedGroup),
      ),
      switchMap(([{ groupId, contentId }, content, group]) => {
        if (typeof content?.message !== 'undefined' || !contentId) return EMPTY;
        return from(this.goalMateProgramDB.getDayPrivateData(contentId)).pipe(
          map((content) => {
            const updatedContents = group!.contents.map((c) => {
              if (c.id !== contentId) return c;
              return { ...c, ...content };
            }) as GoalMateProgramDay[];
            return ContentActions.updateContent({
              content: {
                id: groupId,
                changes: { contents: updatedContents },
              },
            });
          }),
          catchError((error) => {
            console.error('Error', error);
            return of(
              ContentActions.loadContentsError({
                error: error.code || 'Server error',
              }),
            );
          }),
        );
      }),
    );
  });
}
